/**
 * Runtime configuration, read from /config.json at startup.
 *
 * Upstream reads `import.meta.env.VITE_SUPABASE_URL`, which Vite substitutes
 * into the bundle at BUILD time — the API endpoint is then frozen into the
 * image and changing it requires a rebuild. This platform has been bitten by
 * exactly that (HERMES_DASHBOARD_PUBLIC_URL, a URL baked where nothing could
 * reach it), so the value is fetched instead: one image, any environment, and
 * it is readable in a file rather than buried in a hashed chunk.
 *
 * Loaded once and memoised. Everything that needs it awaits the same promise,
 * so a slow first fetch cannot produce two different configs in one session.
 */

export type CrmConfig = {
  /** PostgREST base, e.g. "/api/crm" (same-origin, proxied by the portal). */
  apiUrl: string;
  keycloakUrl: string;
  keycloakRealm: string;
  clientId: string;
  /** Portal origin — mints the PostgREST token and takes attachment uploads. */
  portalUrl: string;
};

let cached: Promise<CrmConfig> | null = null;

export const getCrmConfig = (): Promise<CrmConfig> => {
  if (!cached) {
    cached = fetch("/config.json", { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`config.json: HTTP ${r.status}`);
        return r.json();
      })
      .then((c: Partial<CrmConfig>) => {
        // Fail loudly rather than defaulting. A CRM that quietly points at the
        // wrong origin is worse than one that refuses to start, because the
        // first symptom would be an empty contact list that looks like data
        // loss.
        if (!c.apiUrl) throw new Error("config.json has no apiUrl");
        return {
          apiUrl: c.apiUrl.replace(/\/$/, ""),
          keycloakUrl: (c.keycloakUrl ?? "").replace(/\/$/, ""),
          keycloakRealm: c.keycloakRealm ?? "privateassistant",
          clientId: c.clientId ?? "crm",
          portalUrl: (c.portalUrl ?? "").replace(/\/$/, ""),
        };
      })
      .catch((e) => {
        // NEVER KEEP A REJECTED PROMISE HERE.
        //
        // Memoising the promise is right — a slow first fetch must not produce
        // two different configs in one session. Memoising a FAILED one is not:
        // every later caller gets the same rejection, for the life of the
        // page, with no way back except a reload.
        //
        // What that looks like from a chair: `login()` awaits this before it
        // can build the authorize URL, so one transient failure — a blip, a
        // moment offline, a service worker mid-activation — turns the Sign in
        // button into a button that DOES NOTHING. No error, no navigation,
        // nothing in any server log, because the request is never made. It
        // cost an afternoon.
        //
        // Clearing the cache makes the next attempt a real attempt, which is
        // all a person clicking a second time expects.
        cached = null;
        throw e;
      });
  }
  return cached;
};

/** Test seam: reset the memoised config. */
export const resetCrmConfig = () => {
  cached = null;
};
